// PATH: src/components/coverage/CoverageSummary.tsx
import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Card, CardActionArea, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardRounded";
import { tokens } from "../../theme/theme";
import { issueSummary, type IssueTotals } from "./chapterIssues";
import type { CoverageItem } from "../../api/coverage/coverage";
import {
  OFFER_STATES,
  catalogRate,
  coverageTrackLabel,
  formatPercent,
  stageTotals,
  type CatalogTotals,
  type CellState,
  type CoverageMatrix,
  type MatrixSubject,
  type ReviewTotals,
} from "./coverage";

const nf = (n: number) => n.toLocaleString("fr-FR");

const Kpi: React.FC<{
  label: string;
  value: React.ReactNode;
  hint: React.ReactNode;
  accent?: string;
  loading?: boolean;
  /** Lien : la carte entière devient cliquable. */
  to?: string;
}> = ({ label, value, hint, accent, loading, to }) => {
  const body = (
    <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {accent && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: accent }} />}
        <Typography variant="body2" color="text.secondary" fontWeight={550} sx={{ flex: 1 }}>
          {label}
        </Typography>
        {to && !loading && <ArrowForwardIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
      </Stack>
      <Typography sx={{ fontSize: { xs: "1.375rem", sm: "1.75rem" }, fontWeight: 650, letterSpacing: "-0.02em", mt: 0.75, lineHeight: 1.2 }}>
        {loading ? <Skeleton width={70} /> : value}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        {loading ? <Skeleton width="80%" /> : hint}
      </Typography>
    </Box>
  );
  return (
    <Card sx={{ minWidth: 0 }}>
      {to && !loading ? (
        <CardActionArea component={RouterLink} to={to} sx={{ height: "100%" }}>
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  );
};

/** Apparence et sens de chaque état de case (légende, matrice, barres). */
export const STATE_LOOK: Record<CellState, { label: string; bar: string; bg: string; fg: string; hint: string }> = {
  couverte: {
    label: "Couverte",
    bar: tokens.coverage.playable,
    bg: tokens.coverage.playableSoft,
    fg: "#05603A",
    hint: "chaque chapitre de l'ossature est jouable",
  },
  partielle: {
    label: "Partielle",
    bar: tokens.coverage.imported,
    bg: tokens.coverage.importedSoft,
    fg: "#93370D",
    hint: "des chapitres de l'ossature importés, pas tous jouables",
  },
  a_rediger: {
    label: "À rédiger",
    bar: tokens.coverage.none,
    bg: "#F2F4F7",
    fg: tokens.coverage.noneText,
    hint: "ossature publiée, aucun chapitre importé",
  },
  ossature_manquante: {
    label: "Ossature manquante",
    bar: tokens.coverage.missing,
    bg: tokens.coverage.missingSoft,
    fg: tokens.coverage.missingText,
    hint: "offerte au catalogue sans ossature : non couverte",
  },
  hors_catalogue: {
    label: "Hors catalogue",
    bar: "transparent",
    bg: "transparent",
    fg: tokens.coverage.noneText,
    hint: "publiée mais pas offerte au catalogue : jamais comptée",
  },
};

/**
 * Barre des offres par état (couvertes, partielles, à rédiger, sans
 * ossature) : sa longueur totale est le catalogue, rien d'autre.
 */
export const OfferStateBar: React.FC<{ totals: CatalogTotals; height?: number }> = ({ totals, height = 8 }) => {
  // Une seule offre sur 300 doit rester visible : largeur minimale de 3 px.
  const w = (n: number) => (n > 0 && totals.offers > 0 ? `max(3px, ${(n / totals.offers) * 100}%)` : "0%");
  return (
    <Box sx={{ display: "flex", height, borderRadius: 99, overflow: "hidden", bgcolor: tokens.coverage.none }}>
      {OFFER_STATES.map((st) => (
        <Box key={st} sx={{ width: w(totals.byState[st]), bgcolor: STATE_LOOK[st].bar }} />
      ))}
    </Box>
  );
};

/** Barre à deux tons d'une ossature : jouable (vert) puis importé non jouable (orange). */
export const ChapterBar: React.FC<{
  counts: { ossature_chapters: number; ossature_imported: number; ossature_playable: number };
  height?: number;
}> = ({ counts, height = 8 }) => {
  const ratio = (n: number) => (counts.ossature_chapters > 0 ? Math.min(1, n / counts.ossature_chapters) : 0);
  const p = ratio(counts.ossature_playable);
  const i = Math.max(0, ratio(counts.ossature_imported) - p);
  const w = (r: number) => (r > 0 ? `max(3px, ${r * 100}%)` : "0%");
  return (
    <Box sx={{ display: "flex", height, borderRadius: 99, overflow: "hidden", bgcolor: tokens.coverage.none }}>
      <Box sx={{ width: w(p), bgcolor: tokens.coverage.playable }} />
      <Box sx={{ width: w(i), bgcolor: tokens.coverage.imported }} />
    </Box>
  );
};

/** « 3 couvertes · 1 partielle · 2 sans ossature » : seulement les états présents. */
export const stateBreakdown = (t: CatalogTotals): string =>
  OFFER_STATES.filter((st) => t.byState[st] > 0)
    .map((st) => `${nf(t.byState[st])} ${STATE_LOOK[st].label.toLowerCase()}`)
    .join(" · ");

interface Props {
  totals: CatalogTotals;
  matrix: CoverageMatrix;
  /** Offres sans ossature des niveaux visibles, par matière. */
  missing: { subject: MatrixSubject; rows: CoverageItem[] }[];
  loading?: boolean;
  /** Défauts des chapitres importés ; null = non chargés. */
  issues?: IssueTotals | null;
  /** Lien vers la liste des chapitres à compléter. */
  issuesLink?: string;
  /** Relecture humaine ; absente ou non mesurée = carte cachée. */
  review?: ReviewTotals | null;
  /** Lien vers la liste des chapitres à relire. */
  reviewLink?: string;
}

const missingLabel = (r: CoverageItem) => (r.track_id ? `${r.grade_code} ${coverageTrackLabel(r)}` : r.grade_code);

export const CoverageSummary: React.FC<Props> = ({
  totals,
  matrix,
  missing,
  loading,
  issues,
  issuesLink,
  review,
  reviewLink,
}) => {
  const showReview = Boolean(review?.measured);
  const showIssues = Boolean(issues) || loading;
  const reviewHint = review
    ? [
        review.toFix ? `${nf(review.toFix)} à corriger` : null,
        `${nf(review.validated)} validé${review.validated > 1 ? "s" : ""}`,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";
  const cards = 4 + (showIssues ? 1 : 0) + (showReview ? 1 : 0);
  const missingCount = totals.byState.ossature_manquante;
  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr 1fr", md: `repeat(${cards}, 1fr)` },
        }}
      >
        <Kpi
          loading={loading}
          label="Offres du catalogue"
          value={nf(totals.offers)}
          hint={
            "matière × niveau × filière offertes" +
            (totals.outside ? ` · ${nf(totals.outside)} case${totals.outside > 1 ? "s" : ""} hors catalogue non comptée${totals.outside > 1 ? "s" : ""}` : "")
          }
        />
        <Kpi
          loading={loading}
          label="Couverture"
          accent={tokens.coverage.playable}
          value={formatPercent(catalogRate(totals))}
          hint={`${nf(totals.byState.couverte)} / ${nf(totals.offers)} offres couvertes · ${nf(totals.ossature_playable)} / ${nf(totals.ossature_chapters)} chapitres jouables`}
        />
        <Kpi
          loading={loading}
          label="Ossature manquante"
          accent={missingCount ? tokens.coverage.missing : undefined}
          value={nf(missingCount)}
          hint={missingCount ? "offres sans ossature : rien de prévu, non couvertes" : "chaque offre a son ossature"}
        />
        <Kpi
          loading={loading}
          label="À rédiger · partielles"
          accent={tokens.coverage.imported}
          value={`${nf(totals.byState.a_rediger)} · ${nf(totals.byState.partielle)}`}
          hint="ossature sans chapitre importé · ossature en partie jouable"
        />
        {showIssues && (
          <Kpi
            loading={loading || !issues}
            label="À compléter"
            accent={issues?.incomplete ? "error.main" : undefined}
            value={nf(issues?.incomplete ?? 0)}
            hint={issues ? issueSummary(issues) : ""}
            to={issues?.incomplete ? issuesLink : undefined}
          />
        )}
        {showReview && review && (
          <Kpi
            loading={loading}
            label="À relire"
            accent={review.toReview ? "warning.main" : undefined}
            value={nf(review.toReview)}
            hint={`relecture humaine · ${reviewHint}`}
            to={review.toReview ? reviewLink : undefined}
          />
        )}
      </Box>

      {matrix.stages.length > 0 && !loading && (
        <Card sx={{ p: 2.25 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Couverture par étape
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              moyenne des offres du catalogue (une offre sans ossature compte pour 0)
            </Typography>
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: `repeat(${Math.min(matrix.stages.length, 4)}, 1fr)` },
            }}
          >
            {matrix.stages.map((st) => {
              const t = stageTotals(matrix, st);
              return (
                <Tooltip key={st.id || "autres"} title={stateBreakdown(t) || "aucune offre au catalogue"}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {st.name || "Niveaux"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <b>{formatPercent(catalogRate(t))}</b> · {nf(t.byState.couverte)} / {nf(t.offers)} offres
                      </Typography>
                    </Stack>
                    <OfferStateBar totals={t} />
                    {t.byState.ossature_manquante > 0 && (
                      <Typography variant="caption" sx={{ color: tokens.coverage.missingText, mt: 0.5, display: "block" }}>
                        {nf(t.byState.ossature_manquante)} sans ossature
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Card>
      )}

      {missing.length > 0 && !loading && (
        <Card sx={{ p: 2.25, borderLeft: `3px solid ${tokens.coverage.missing}` }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Offres sans ossature
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              offertes au catalogue, aucun chapitre prévu : l'ossature se publie depuis tc_docsourcing
            </Typography>
          </Typography>
          <Stack spacing={0.5}>
            {missing.map(({ subject, rows }) => (
              <Typography key={subject.id} variant="body2">
                <b>{subject.name}</b>
                <Typography component="span" variant="body2" color="text.secondary">
                  {" — "}
                  {rows.map(missingLabel).join(", ")}
                </Typography>
              </Typography>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};

export const Legend: React.FC = () => (
  <Stack direction="row" spacing={2} sx={{ color: "text.secondary" }} flexWrap="wrap" useFlexGap>
    {(["couverte", "partielle", "a_rediger", "ossature_manquante", "hors_catalogue"] as CellState[]).map((st) => (
      <Tooltip key={st} title={STATE_LOOK[st].hint}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: 0.75,
              bgcolor: STATE_LOOK[st].bg,
              border: st === "hors_catalogue" ? `1px dashed ${tokens.borderStrong}` : `1px solid ${STATE_LOOK[st].bar}`,
            }}
          />
          <Typography variant="caption">{STATE_LOOK[st].label}</Typography>
        </Stack>
      </Tooltip>
    ))}
  </Stack>
);

export default CoverageSummary;
